import React, { useState, useMemo, useEffect } from "react";
import { Link } from 'react-router-dom';
import Table from "../../utils/Table";
import AddCommunityModal from "../../components/Modals/AddCommunityModal";
import ImportCommunityModal from "../../components/Modals/ImportCommunityModal";
import { fetchCommunities, deleteCommunity } from "../../services/api";
import ActionButtons from "../../utils/ActionButtons";
import { getStatusDot, formatDate } from "../../utils/TableUtils";
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";

function Community() {
  const [data, setData] = useState([]);
  const [filterInput, setFilterInput] = useState("");
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [modalShow, setModalShow] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchCommunities(pageIndex + 1, 10);
      setData(response?.data);
      setPageCount(response?.pagination?.totalPages);
    } catch (error) {
      console.error("Error fetching communities:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageIndex]);

  const columns = useMemo(
    () => [
      {
        Header: "Name of Community / Inspector",
        accessor: "communityName",
        Cell: ({ row }) => {
          const communityId = row.original.id;
          return (
            <Link to={`/admin/communities/${communityId}`} state={{ communityName: row.values.communityName }}>
              {row.values.communityName}
            </Link>
          );
        },
        sortType: (rowA, rowB) => {
          const a = rowA.values.communityName?.toLowerCase() || "";
          const b = rowB.values.communityName?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Phone Number",
        accessor: "communityPhoneNumber",
        sortType: (rowA, rowB) => {
          const a = rowA.values.communityPhoneNumber?.toLowerCase() || "";
          const b = rowB.values.communityPhoneNumber?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Total Applications",
        accessor: "totalApplications",
        Cell: ({ value }) => value || 0,
        sortType: (rowA, rowB) => {
          const a = rowA.values.totalApplications || 0;
          const b = rowB.values.totalApplications || 0;
          return a > b ? 1 : a < b ? -1 : 0;
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => getStatusDot(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.status?.toLowerCase() || "";
          const b = rowB.values.status?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Products",
        accessor: "hasSimplePermit",
        Cell: ({ row }) => (
          <div className="d-flex gap-1 flex-wrap">
            {row.original.hasSimplePermit ? <span className="badge bg-primary">Permit</span> : null}
            {row.original.hasSimpleSpect ? <span className="badge bg-info">Spect</span> : null}
            {row.original.hasSimpleCheck ? <span className="badge bg-success">Check</span> : null}
          </div>
        ),
        disableSortBy: true,
      },
      {
        Header: "Date Created",
        accessor: "createdAt",
        Cell: ({ value }) => formatDate(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.createdAt?.toLowerCase() || "";
          const b = rowB.values.createdAt?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: ({ row }) => (
          <ActionButtons
            onEdit={handleEdit}
            onDelete={handleDelete}
            rowData={row.original}
            showDelete={true}
          />
        ),
        disableSortBy: true,
      },
    ],
    [pageIndex]
  );

  const handleEdit = (community) => {
    console.log("Editing:", community);
    setSelectedCommunity(community);
    setModalShow(true);
  };

  const handleModalClose = (refresh) => {
    setModalShow(false);
    if (refresh === true) {
      fetchData();
    }
  };

  const refrehhData = async () => {
    setShowImportModal(false);
    fetchData();
  }

  const handleDelete = async (rowData) => {
    startLoading();
    try {
      const { id } = rowData;
      if (!id) {
        showToast("Invalid ID, please try again.", "error");
        return;
      }
      const response = await deleteCommunity(id);

      if (response && response.message) {
        setData((prevData) =>
          prevData.filter((community) => community.id !== id)
        );
        showToast(response.message, "success");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("An error occurred while deleting the community.", "error");
      }
    } finally {
      stopLoading();
    }
  };

  const handlePageChange = (newPageIndex) => {
    setPageIndex(newPageIndex);
  };

  const showAddCommunityModal = () => {
    setSelectedCommunity(null);
    setModalShow(true);
  };

  const showImportCommunityModal = () => {
    setShowImportModal(true);
  };


  return (
    <div className="subMain-content">
      <div>
        <h1 className="mt-4">Communities</h1>
      </div>

      <Table
        columns={columns}
        data={data}
        filterInput={filterInput}
        setFilterInput={setFilterInput}
        pageCount={pageCount}
        loading={loading}
        handleEdit={handleEdit}
        onPageChange={handlePageChange}
        currentPage={pageIndex}
        showActionButton="true"
        onTrigger={showAddCommunityModal}
        onImportTrigger={showImportCommunityModal}
        actionButtonLabel="Add Community"
      />

      <AddCommunityModal
        show={modalShow}
        onHide={handleModalClose}
        community={selectedCommunity}
      />
       <ImportCommunityModal
        show={showImportModal}
        onHide={() => refrehhData()}
      />
    </div>
  );
}

export default Community;
